import * as THREE from 'three';
import React from 'react';
import { correctAxisData } from '../../utils';
import * as constantsViewer from '../../constants';
import data from './data/rod';

export function Rod({
  shelfHeight = 370,
  cabinetWidth = 1000,
  cabinetDepth = 420,
  unselected = false,
}) {
  return (
    <group>
      <RodSupport
        rodSupportPosition="L"
        shelfHeight={shelfHeight}
        cabinetWidth={cabinetWidth}
        cabinetDepth={cabinetDepth}
        unselected={unselected}
      />
      <RodSupport
        rodSupportPosition="R"
        shelfHeight={shelfHeight}
        cabinetWidth={cabinetWidth}
        cabinetDepth={cabinetDepth}
        unselected={unselected}
      />
      <RodMainPiece
        shelfHeight={shelfHeight}
        cabinetWidth={cabinetWidth}
        cabinetDepth={cabinetDepth}
        unselected={unselected}
      />
    </group>
  );
}

function RodSupport({
  rodSupportPosition = 'L',
  shelfHeight = 410,
  cabinetWidth = 1000,
  cabinetDepth = 420,
  unselected = false,
}) {
  var modelCabinetWidth = 999;
  var modelCabinetDepth = 419;
  var modelshelfHeight = 410;

  var cabinetDiffWith = cabinetWidth - modelCabinetWidth;
  var cabinetDiffDepth = cabinetDepth - modelCabinetDepth;

  let xDiff = 0;
  let yDiff = 0;
  let zDiff = 0;

  let rotation = [0, 0, 0];

  yDiff += (shelfHeight - modelshelfHeight) / 2;

  switch (rodSupportPosition) {
    case 'L':
      xDiff -= cabinetDiffWith / 2;
      zDiff += cabinetDiffDepth / 2;
      break;

    case 'R':
      xDiff += modelCabinetWidth + cabinetDiffWith / 2;
      zDiff += cabinetDiffDepth / 2;

      // Special case due to group rotation
      xDiff -= cabinetWidth - 4;
      zDiff -= cabinetDiffDepth / 2;
      zDiff -= cabinetDiffDepth / 2;
      zDiff += 10;

      rotation = [0, Math.PI, 0];
      break;

    default:
      break;
  }
  return (
    <group rotation={rotation}>
      <RodSupportTop
        xDiff={xDiff}
        yDiff={yDiff}
        zDiff={zDiff}
        unselected={unselected}
      />
      <RodSupportMiddle
        xDiff={xDiff}
        yDiff={yDiff}
        zDiff={zDiff}
        unselected={unselected}
      />
      <RodSupportBottom
        xDiff={xDiff}
        yDiff={yDiff}
        zDiff={zDiff}
        unselected={unselected}
      />
    </group>
  );
}

function RodMainPiece({
  shelfHeight = 410,
  cabinetWidth = 1000,
  cabinetDepth = 420,
  unselected = false,
}) {
  var modelCabinetWidth = 999;
  var modelCabinetDepth = 419;
  var modelshelfHeight = 410;

  var cabinetDiffDepth = cabinetDepth - modelCabinetDepth;

  var scaleX = cabinetWidth / modelCabinetWidth;
  scaleX = (999 * scaleX - 54) / 945;

  var vertices = new Float32Array(data.rod.vertices);
  var normals = new Float32Array(data.rod.normals);

  var uv_coord = new Float32Array(data.rod.uv_coord);

  /* 
        Remove this in meshPhongMaterial because onClick fired twice:
          side={THREE.DoubleSide} 
        */

  /*
        Other method to apply lookAt method on camera:
          const { camera } = useThree()
          camera.lookAt(new THREE.Vector3(0.00000, 0.00000, 0.00000)) 
        */
  return (
    <mesh
      receiveShadow
      castShadow
      onUpdate={self => {
        var center = new THREE.Vector3();
        self.geometry.computeBoundingBox();
        self.geometry.boundingBox.getCenter(center);
        self.geometry.center();
        self.position.copy(center);

        self.position.z += cabinetDiffDepth / 2;
        self.geometry.scale(scaleX, 1, 1);
        self.position.y += (shelfHeight - modelshelfHeight) / 2;
      }}
    >
      <bufferGeometry attach="geometry">
        <bufferAttribute
          attachObject={['attributes', 'position']}
          count={vertices.length / 3}
          array={correctAxisData(vertices)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'normal']}
          count={normals.length / 3}
          array={correctAxisData(normals)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'uv']}
          count={uv_coord.length / 2}
          array={uv_coord}
          itemSize={2}
        />
      </bufferGeometry>
      <meshPhongMaterial
        attach="material"
        color={0xffffff}
        specular={0xffffff}
        shininess={16}
        opacity={unselected ? constantsViewer.OPACITY_UNSELECTED_SHELF : 1}
        transparent
      />
    </mesh>
  );
}

function RodSupportBottom({
  xDiff = 0,
  yDiff = 0,
  zDiff = 0,
  unselected = false,
}) {
  var vertices = new Float32Array(data.rodSupportBottom.vertices);
  var normals = new Float32Array(data.rodSupportBottom.normals);

  var uv_coord = new Float32Array(data.rodSupportBottom.uv_coord);

  /* 
        Remove this in meshPhongMaterial because onClick fired twice:
          side={THREE.DoubleSide} 
        */

  /*
        Other method to apply lookAt method on camera:
          const { camera } = useThree()
          camera.lookAt(new THREE.Vector3(0.00000, 0.00000, 0.00000)) 
        */
  return (
    <mesh
      receiveShadow
      castShadow
      onUpdate={self => {
        var center = new THREE.Vector3();
        self.geometry.computeBoundingBox();
        self.geometry.boundingBox.getCenter(center);
        self.geometry.center();
        self.position.copy(center);

        self.position.x += xDiff;
        self.position.y += yDiff;
        self.position.z += zDiff;
      }}
    >
      <bufferGeometry attach="geometry">
        <bufferAttribute
          attachObject={['attributes', 'position']}
          count={vertices.length / 3}
          array={correctAxisData(vertices)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'normal']}
          count={normals.length / 3}
          array={correctAxisData(normals)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'uv']}
          count={uv_coord.length / 2}
          array={uv_coord}
          itemSize={2}
        />
      </bufferGeometry>
      <meshPhongMaterial
        attach="material"
        color={0xffffff}
        specular={0xffffff}
        shininess={16}
        opacity={unselected ? constantsViewer.OPACITY_UNSELECTED_SHELF : 1}
        transparent
      />
    </mesh>
  );
}

function RodSupportMiddle({
  xDiff = 0,
  yDiff = 0,
  zDiff = 0,
  unselected = false,
}) {
  var vertices = new Float32Array(data.rodSupportMiddle.vertices);
  var normals = new Float32Array(data.rodSupportMiddle.normals);

  var uv_coord = new Float32Array(data.rodSupportMiddle.uv_coord);

  /* 
        Remove this in meshPhongMaterial because onClick fired twice:
          side={THREE.DoubleSide} 
        */

  /*
        Other method to apply lookAt method on camera:
          const { camera } = useThree()
          camera.lookAt(new THREE.Vector3(0.00000, 0.00000, 0.00000)) 
        */
  return (
    <mesh
      receiveShadow
      castShadow
      onUpdate={self => {
        var center = new THREE.Vector3();
        self.geometry.computeBoundingBox();
        self.geometry.boundingBox.getCenter(center);
        self.geometry.center();
        self.position.copy(center);

        self.position.x += xDiff;
        self.position.y += yDiff;
        self.position.z += zDiff;
      }}
    >
      <bufferGeometry attach="geometry">
        <bufferAttribute
          attachObject={['attributes', 'position']}
          count={vertices.length / 3}
          array={correctAxisData(vertices)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'normal']}
          count={normals.length / 3}
          array={correctAxisData(normals)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'uv']}
          count={uv_coord.length / 2}
          array={uv_coord}
          itemSize={2}
        />
      </bufferGeometry>
      <meshPhongMaterial
        attach="material"
        color={0xffffff}
        specular={0xffffff}
        shininess={16}
        opacity={unselected ? constantsViewer.OPACITY_UNSELECTED_SHELF : 1}
        transparent
      />
    </mesh>
  );
}

function RodSupportTop({
  xDiff = 0,
  yDiff = 0,
  zDiff = 0,
  unselected = false,
}) {
  var vertices = new Float32Array(data.rodSupportTop.vertices);
  var normals = new Float32Array(data.rodSupportTop.normals);

  var uv_coord = new Float32Array(data.rodSupportTop.uv_coord);

  /* 
        Remove this in meshPhongMaterial because onClick fired twice:
          side={THREE.DoubleSide} 
        */

  /*
        Other method to apply lookAt method on camera:
          const { camera } = useThree()
          camera.lookAt(new THREE.Vector3(0.00000, 0.00000, 0.00000)) 
        */
  return (
    <mesh
      receiveShadow
      castShadow
      onUpdate={self => {
        var center = new THREE.Vector3();
        self.geometry.computeBoundingBox();
        self.geometry.boundingBox.getCenter(center);
        self.geometry.center();
        self.position.copy(center);

        self.position.x += xDiff;
        self.position.y += yDiff;
        self.position.z += zDiff;
      }}
    >
      <bufferGeometry attach="geometry">
        <bufferAttribute
          attachObject={['attributes', 'position']}
          count={vertices.length / 3}
          array={correctAxisData(vertices)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'normal']}
          count={normals.length / 3}
          array={correctAxisData(normals)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'uv']}
          count={uv_coord.length / 2}
          array={uv_coord}
          itemSize={2}
        />
      </bufferGeometry>
      <meshPhongMaterial
        attach="material"
        color={0xffffff}
        specular={0xffffff}
        shininess={16}
        opacity={unselected ? constantsViewer.OPACITY_UNSELECTED_SHELF : 1}
        transparent
      />
    </mesh>
  );
}
